/* eslint-disable no-console */
import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'uxd-design-system-components';
import './SendResultsButton.scss';
import AddContactFormModal from '../AddContactFormModal/AddContactFormModal';
import CommentModal from '../CommentModal/CommentModal';
import useQuery from '../../utils/useQuery/useQuery';
import { sendToDelegator } from '../../api/index';
import getConType from '../../utils/getConType/getConType';
import getSource from '../../utils/getSource/getSource';
import { LocaleContext } from '../../contexts';
import getText from '../../assets/locale/locale';
import addDataLayerTrigger from '../../utils/addDataLayerTrigger/addDataLayerTrigger';

const SendResultsButton = ({
  sendingPdfToFa,
  setSendingPdfToFa,
  setResultsSentToFa,
  sendResultsButtonDisabled,
  resultsToSendToDelegator,
  setNotificationContent,
  setNotificationOpen,
  query,
  sessionId,
  coldContactData,
  setColdContactData,
  faInfo,
  contactName,
  userComments,
  setUserComments,
}) => {
  const urlQuery = useQuery();
  const locId = useContext(LocaleContext);
  const [displayColdContactModal, setDisplayColdContactModal] = useState(false);
  const [displayCommentModal, setDisplayCommentModal] = useState(false);
  const [branchNo, setBranchNo] = useState(null);

  const conId = urlQuery.get('conId');
  const faNo = urlQuery.get('faNo') || faInfo.faNo;
  const faNoParam = urlQuery.get('faNo');
  const entityID = urlQuery.get('EID');
  const faFullName = `${faInfo.faFirstName} ${faInfo.faLastName}`;

  useEffect(() => {
    setBranchNo(faInfo.branchNo);
  }, [faInfo]);

  const successNotificationHandler = () => {
    if (coldContactData) {
      if (faInfo.faFirstName && faInfo.faLastName) {
        return getText(
          locId,
          'Results',
          'coldContactSuccessMessage',
          'SendResultsButton'
        ).replace('%FA_NAME%', faFullName);
      }
      return getText(
        locId,
        'Results',
        'coldContactSuccessMessage',
        'SendResultsButton'
      ).replace(
        '%FA_NAME%',
        getText(locId, 'Results', 'defaultText', 'SendResultsButton')
      );
    }
    return getText(
      locId,
      'Results',
      'successNotification',
      'SendResultsButton'
    );
  };

  const sendResultsToFA = async () => {
    setSendingPdfToFa(true);
    const notifyFaInd = 'Y';
    const storeToDbInd = 'Y';
    const archiveInd = coldContactData ? 'N' : 'Y';
    const utmSource = urlQuery.get('utm_source');
    const utmContent = urlQuery.get('utm_content');
    const conType = getConType(utmSource, utmContent);
    const sourceForDelegator = getSource(utmSource, entityID, faNoParam, conId);

    addDataLayerTrigger('email-fa-click', {
      faId: faNo,
    });

    try {
      const data = {
        faNo,
        contactNo: urlQuery.get('conId'),
        branchNo,
        results: resultsToSendToDelegator,
        persona: query,
        sessionId,
        coldContactData,
        userComments,
      };
      await sendToDelegator({
        data,
        locId,
        notifyFaInd,
        storeToDbInd,
        conType,
        utmSource: sourceForDelegator,
        archiveInd,
      });

      setResultsSentToFa(true);
      setNotificationContent({
        type: 'confirmation',
        text: successNotificationHandler(),
      });
      setNotificationOpen(true);
      addDataLayerTrigger('email-confirmation');
    } catch (error) {
      console.error(error);
      setNotificationContent({
        type: 'error',
        text: coldContactData
          ? getText(
              locId,
              'Results',
              'coldContactErrorMessage',
              'SendResultsButton'
            ).replace('%FA_FIRSTNAME%', faInfo.faFirstName)
          : getText(locId, 'Results', 'errorMsg', 'SendResultsButton'),
      });
      setNotificationOpen(true);
    }

    setSendingPdfToFa(false);
  };

  const sendResultsToFAButtonHandler = () => {
    if (conId && conId !== '0') {
      setDisplayCommentModal(true);
    } else {
      setDisplayColdContactModal(true);
    }
  };

  return (
    <>
      <Button
        label={
          sendResultsButtonDisabled
            ? getText(locId, 'Results', 'successButton', 'SendResultsButton')
            : getText(locId, 'Results', 'sendButton', 'SendResultsButton')
        }
        disabled={sendResultsButtonDisabled}
        styleVariant="reversed-secondary"
        loading={sendingPdfToFa}
        attributes={{
          id: sendResultsButtonDisabled
            ? 'results-to-fa-complete'
            : 'results-send-to-fa',
          'data-testid': sendResultsButtonDisabled
            ? 'send-fa-disabled'
            : 'results-send-to-fa',
        }}
        icon={sendResultsButtonDisabled ? '' : 'message-send'}
        onClick={sendResultsToFAButtonHandler}
      />
      <AddContactFormModal
        displayColdContactModal={displayColdContactModal}
        setDisplayColdContactModal={setDisplayColdContactModal}
        sendResultsToFA={sendResultsToFA}
        coldContactData={coldContactData}
        setColdContactData={setColdContactData}
        faInfo={faInfo}
      />
      <CommentModal
        displayCommentModal={displayCommentModal}
        setDisplayCommentModal={setDisplayCommentModal}
        setUserComments={setUserComments}
        sendResultsToFA={sendResultsToFA}
        faInfo={faInfo}
        contactName={contactName}
      />
    </>
  );
};

SendResultsButton.propTypes = {
  sendResultsButtonDisabled: PropTypes.bool.isRequired,
  sendingPdfToFa: PropTypes.bool.isRequired,
  setSendingPdfToFa: PropTypes.func.isRequired,
  setResultsSentToFa: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  resultsToSendToDelegator: PropTypes.object.isRequired,
  setNotificationContent: PropTypes.func.isRequired,
  setNotificationOpen: PropTypes.func.isRequired,
  query: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  sessionId: PropTypes.string.isRequired,
  coldContactData: PropTypes.objectOf(PropTypes.string),
  setColdContactData: PropTypes.func.isRequired,
  faInfo: PropTypes.objectOf(PropTypes.string).isRequired,
  contactName: PropTypes.objectOf(PropTypes.string),
  userComments: PropTypes.string,
  setUserComments: PropTypes.func.isRequired,
};
SendResultsButton.defaultProps = {
  coldContactData: {},
  contactName: {},
  userComments: '',
};
export default SendResultsButton;
